import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { alertDialog, confirmDialog, guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { download } from '@adornis/file-utils/client/helper-methods.js';
import { removeFile } from '@adornis/file-utils/db/api/files.operations.js';
import { type AdornisFile, AdornisFileMeta } from '@adornis/file-utils/db/files.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { ContractAPI } from '../../api/ContractAPI.js';
import { CONTRACT_DOCUMENT_SELECTIONSET } from '../../constants/contractDocumentSelectionSet.js';
import { PERSON_DOCUMENT_SELECTIONSET } from '../../constants/personDocumentSelectionSet.js';
import type { AppliedContract } from '../../entities/AppliedContract.js';
import { ContractFile } from '../../entities/ContractFile.js';
import { ContractSupplement } from '../../entities/ContractSupplement.js';
import { ContractSupplementFile } from '../../entities/ContractSupplementFile.js';

interface ContractDocumentManagerDialogProps {
  contract: Maybe<AppliedContract>;
  supplement: Maybe<ContractSupplement>;
}

const templateFieldsString = () =>
  JSON.stringify(
    {
      ...PERSON_DOCUMENT_SELECTIONSET,
      mitarbeiterDaten: {
        ...PERSON_DOCUMENT_SELECTIONSET.mitarbeiterDaten,
        aktiveVertragsdaten: {
          ...CONTRACT_DOCUMENT_SELECTIONSET,
          nachtragsNummer: 1,
          unternachtragsNummer: 1,
          nachtragInfos: selectionSet(() => ContractSupplement, 2),
        },
      },
    },
    null,
    2,
  );

const TemplateFieldsDownloadButton = (fileName: string) => html`
  <button
    class="btn btn-ghost btn-sm btn-square"
    aria-label="Templatefelder herunterladen"
    @click=${() => {
      download(templateFieldsString(), fileName, 'text/plain');
    }}
  >
    <span class="icon-[material-symbols--dataset-linked-outline] text-success text-lg"></span>
  </button>
`;

const DocumentCard = (
  document: NarrowedEntity<AdornisFile, { _id: 1; meta: { completeName: 1 } }>,
  documentClass: typeof AdornisFile,
) => html`
  <div class="card flex-row items-center justify-between gap-2 p-3">
    <span class="font-semibold break-all"> ${document.meta?.completeName} </span>
    <div class="flex flex-row items-center gap-2">
      <button
        class="btn btn-ghost btn-sm btn-square"
        aria-label="Dokument herunterladen"
        @click=${() => goTo(documentClass.getDownloadLink(document._id), { target: '_blank' })}
      >
        <span class="icon-[material-symbols--download] text-success text-lg"></span>
      </button>
      <button
        class="btn btn-ghost btn-sm btn-square"
        aria-label="Dokument löschen"
        @click=${async () => {
          if (!(await confirmDialog('Möchten Sie die Datei wirklich löschen?'))) return;

          await guardDialog(async () => {
            await removeFile(documentClass._class, document._id)();
          });
        }}
      >
        <span class="icon-[material-symbols--delete-outline] text-error text-lg"></span>
      </button>
    </div>
  </div>
`;

const ContractDocumentManagerDialogContent = stateful(
  (props: ContractDocumentManagerDialogProps & { resolve: () => void }) => {
    const [tab, setTab] = useState<'contract' | 'supplement'>('contract');
    const [generating, setGenerating] = useState(false);

    const { value: contractFiles, loading: contractFilesLoading } = useQuery<
      Array<NarrowedEntity<AdornisFile, { _id: 1; meta: { completeName: 1; fileName: 1; extension: 1 } }>>
    >(
      () =>
        ContractAPI.subscribeAllContractDocuments(props.contract?._id ?? '')({
          _id: 1,
          meta: { completeName: 1, fileName: 1, extension: 1 },
        }),
      [props.contract?._id],
    );

    const { value: supplementFiles, loading: supplementFilesLoading } = useQuery<
      Array<NarrowedEntity<AdornisFile, { _id: 1; meta: { completeName: 1; fileName: 1; extension: 1 } }>>
    >(
      () =>
        ContractAPI.subscribeAllContractSupplementDocuments(props.supplement?._id ?? '')({
          _id: 1,
          meta: { completeName: 1, fileName: 1, extension: 1 },
        }),
      [props.supplement?._id],
    );

    if (contractFilesLoading || supplementFilesLoading) {
      return html`<div class="flex justify-center p-8"><div class="loading loading-spinner loading-lg"></div></div>`;
    }

    const files = tab === 'contract' ? (contractFiles ?? []) : (supplementFiles ?? []);
    const documentClass = tab === 'contract' ? ContractFile : ContractSupplementFile;

    const generateContractDocument = async (onlyInfo: boolean) => {
      setGenerating(true);

      if (!props.contract?._id) return;
      try {
        const file = await ContractAPI.generateContractDocx(
          props.contract._id,
          props.contract.agInfos?.einstellungsdatum ?? new Date(),
          onlyInfo,
        )({ _id: 1, meta: selectionSet(() => AdornisFileMeta, 1) });
        if (!file) throw new Error('Template generation failed!');

        goTo(ContractFile.getDownloadLink(file._id), { target: '_blank' });
      } catch (error: unknown) {
        if (error instanceof Error) {
          void alertDialog(error.message);
        }
      }

      setGenerating(false);
    };

    const generateSupplementDocument = async () => {
      setGenerating(true);

      if (!props.contract?._id || !props.contract.appliedDate || !props.supplement?._id) return;
      try {
        // get the max date of the supplement. We want to apply the the contract to the maximum date of the supplement so all the changes are included. The endpoint calls the apply function with includeDrafts = true so we also get the supplements which have no signature yet.
        const maxDate = [
          props.supplement.startDateGeneral,
          props.supplement.startDateAG1,
          props.supplement.startDateAG2,
        ]
          .filter(isNotNullish)
          .sort((a, b) => b.getTime() - a.getTime())
          .at(0);

        if (!maxDate)
          throw new ExpectedError(
            'Es wurde kein Startdatum für den Nachtrag gefunden. Ohne Startdatum kann kein Dokument generiert werden.',
          );

        const file = await ContractAPI.generateSupplementDocx(
          props.contract._id,
          maxDate,
          props.supplement._id,
        )({ _id: 1, meta: selectionSet(() => AdornisFileMeta, 1) });

        goTo(ContractSupplementFile.getDownloadLink(file._id), { target: '_blank' });
      } catch (error: unknown) {
        if (error instanceof Error) {
          void alertDialog(error.message);
        }
      }

      setGenerating(false);
    };

    return html`
      <div class="dialog-container items-center">
        <div role="tablist" class="tabs tabs-border">
          <a role="tab" class="tab ${tab === 'contract' ? 'tab-active' : ''}" @click=${() => setTab('contract')}>
            Vertrag
          </a>
          ${
            props.supplement
              ? html`
                  <a
                    role="tab"
                    class="tab ${tab === 'supplement' ? 'tab-active' : ''}"
                    @click=${() => setTab('supplement')}
                  >
                    Nachtrag
                  </a>
                `
              : nothing
          }
        </div>

        <div class="flex w-full flex-col gap-2">
          ${
            !files.length && !generating
              ? html`<p class="text-center text-base-content/70">Es sind keine Dokumente hinterlegt.</p>`
              : repeat(
                  files,
                  file => file._id,
                  file => DocumentCard(file, documentClass),
                )
          }
          ${generating ? html`<div class="skeleton h-12 w-full"></div>` : nothing}
        </div>

        <div class="flex flex-row flex-wrap items-center gap-2">
          ${
            tab === 'contract'
              ? html`
                  <button
                    class="btn btn-primary"
                    ?disabled=${generating}
                    @click=${() => generateContractDocument(true)}
                  >
                    ${generating ? html`<span class="loading loading-spinner loading-xs"></span>` : nothing}
                    Mitarbeiterinformationen drucken
                  </button>
                  <button
                    class="btn btn-primary"
                    ?disabled=${generating}
                    @click=${() => generateContractDocument(false)}
                  >
                    ${generating ? html`<span class="loading loading-spinner loading-xs"></span>` : nothing} Template
                    aus Vertrag befüllen
                  </button>
                  ${TemplateFieldsDownloadButton('Felder Vertragstemplate.txt')}
                `
              : html`
                  <button class="btn btn-primary" ?disabled=${generating} @click=${() => generateSupplementDocument()}>
                    ${generating ? html`<span class="loading loading-spinner loading-xs"></span>` : nothing} Template
                    aus Nachtrag befüllen
                  </button>
                  ${TemplateFieldsDownloadButton('Felder Nachtragstemplate.txt')}
                `
          }
        </div>
      </div>
    `;
  },
);

/** Opens the dialog to view, generate and delete the documents of a contract and its selected supplement. */
export async function showContractDocumentManagerDialog(props: ContractDocumentManagerDialogProps): Promise<void> {
  try {
    await showDialog<void>({
      content: resolve => ContractDocumentManagerDialogContent({ ...props, resolve: () => resolve() }),
      modalBoxClasses: 'max-w-3xl',
    });
  } catch {
    // dialog dismissed
  }
}
